import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from "react-native";
import { appConfig } from "../config/appConfig.js";
import {
  addDomain,
  getAdvancedMediaKeywordsEnabled,
  getBlocklist,
  getFilterEnabled,
  getGoogleYoutubeSafeModeEnabled,
  getNextDnsToken,
  removeDomain,
  setAdvancedMediaKeywordsEnabled,
  setFilterEnabled,
  setGoogleYoutubeSafeModeEnabled,
  setNextDnsToken,
} from "../blocking/BlocklistManager";
import { buildParentalVpnSyncPayload } from "../blocking/parentalVpnPayload";
import { getAdvancedMediaEntertainmentKeywords } from "../blocking/advancedMediaKeywords";
import { syncRules, startProtection, stopProtection } from "../platform/screenTimeBridge";
import { saveServerBlocklist, isBlocklistApiConfigured } from "../services/blocklistApi";
import { isIosFamilyControlsBlockingUiEnabled } from "../utils/featureFlags";
import { FamilyControlsBlockingSection } from "./FamilyControlsBlockingSection";
import { LockboxLabPanel } from "./LockboxLabPanel";

const DEMO_USER_ID = "demo-user";

const iosBlockingUiEnabled = isIosFamilyControlsBlockingUiEnabled();

function nextDnsSetupUrl(token) {
  const base = appConfig.nextDnsConfigUrl || "https://my.nextdns.io";
  const clean = String(token || "").trim();
  if (!clean) return base;
  return `${base.replace(/\/$/, "")}/${encodeURIComponent(clean)}/setup`;
}

export function ParentalFilterPanel() {
  const [domainInput, setDomainInput] = useState("");
  const [domains, setDomains] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [advancedMediaKeywords, setAdvancedMediaKeywords] = useState(false);
  const [safeMode, setSafeMode] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nextDnsToken, setToken] = useState("");

  useEffect(() => {
    (async () => {
      const [storedDomains, storedEnabled, advancedOn, safeOn, token] = await Promise.all([
        getBlocklist(),
        getFilterEnabled(),
        getAdvancedMediaKeywordsEnabled(),
        getGoogleYoutubeSafeModeEnabled(),
        getNextDnsToken(),
      ]);
      setDomains(storedDomains);
      setEnabled(storedEnabled);
      setAdvancedMediaKeywords(advancedOn);
      setSafeMode(safeOn);
      setToken(token);
    })();
  }, []);

  const profileUrl = useMemo(() => nextDnsSetupUrl(nextDnsToken), [nextDnsToken]);

  const advancedKeywordsPreview = useMemo(() => getAdvancedMediaEntertainmentKeywords().join(", "), []);

  const syncEverywhere = async (nextDomains, nextEnabled, advancedMediaOpt) => {
    const advancedOn =
      typeof advancedMediaOpt === "boolean" ? advancedMediaOpt : await getAdvancedMediaKeywordsEnabled();
    const payload = await buildParentalVpnSyncPayload(nextDomains, advancedOn);
    await syncRules(payload.block, payload.allow, payload.keywords, payload.categoryRules, {
      safeMode: payload.safeMode,
    });
    if (Platform.OS === "android") {
      if (nextEnabled) {
        await startProtection();
      } else {
        await stopProtection();
      }
    }
    try {
      // Best effort remote sync when EXPO_PUBLIC_BLOCKLIST_API_URL is set.
      if (isBlocklistApiConfigured()) {
        await saveServerBlocklist(DEMO_USER_ID, nextDomains);
      }
    } catch {
      // Offline-first: local cache still works.
    }
  };

  const onToggleSafeMode = async (value) => {
    setSaving(true);
    try {
      await setGoogleYoutubeSafeModeEnabled(value);
      setSafeMode(value);
      await syncEverywhere(domains, enabled, advancedMediaKeywords);
    } catch (e) {
      Alert.alert("Safe Mode", e?.message ? String(e.message) : "Could not update Safe Mode.");
    } finally {
      setSaving(false);
    }
  };

  const onToggleAdvancedKeywords = async (value) => {
    setSaving(true);
    try {
      await setAdvancedMediaKeywordsEnabled(value);
      setAdvancedMediaKeywords(value);
      await syncEverywhere(domains, enabled, value);
    } catch (e) {
      Alert.alert("Advanced filter", e?.message ? String(e.message) : "Could not update advanced option.");
    } finally {
      setSaving(false);
    }
  };

  const onAdd = async () => {
    if (!domainInput.trim()) return;
    setSaving(true);
    try {
      const next = await addDomain(domainInput);
      setDomains(next);
      setDomainInput("");
      await syncEverywhere(next, enabled, advancedMediaKeywords);
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (domain) => {
    setSaving(true);
    try {
      const next = await removeDomain(domain);
      setDomains(next);
      await syncEverywhere(next, enabled);
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async () => {
    const nextEnabled = !enabled;
    setSaving(true);
    try {
      await setFilterEnabled(nextEnabled);
      setEnabled(nextEnabled);
      await syncEverywhere(domains, nextEnabled, advancedMediaKeywords);
    } catch (e) {
      Alert.alert("Filter", e?.message ? String(e.message) : "Could not change filter state.");
    } finally {
      setSaving(false);
    }
  };

  const onSaveToken = async () => {
    await setNextDnsToken(nextDnsToken);
    Alert.alert("Saved", "DNS configuration token stored on device.");
  };

  const openProfile = async () => {
    const can = await Linking.canOpenURL(profileUrl);
    if (!can) {
      Alert.alert("DNS profile", "Could not open DNS setup URL.");
      return;
    }
    await Linking.openURL(profileUrl);
  };

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Parental DNS Filter</Text>
      <Text style={styles.lockboxHint}>
        Default: adult websites only. Add any site here (youtube.com, etc.) to block it in the browser. To block apps by genre (TikTok, etc.), use Home → Lockbox Lab.
      </Text>

      <View style={styles.row}>
        <Text style={styles.label}>Filter status</Text>
        <Pressable onPress={onToggle} disabled={saving} style={[styles.toggle, enabled ? styles.toggleOn : null]}>
          <Text style={styles.toggleText}>{enabled ? "ON" : "OFF"}</Text>
        </Pressable>
      </View>

      <Text style={styles.label}>Add blocked site</Text>
      <View style={styles.inputRow}>
        <TextInput
          value={domainInput}
          onChangeText={setDomainInput}
          placeholder="example.com"
          autoCapitalize="none"
          style={styles.input}
        />
        <Pressable onPress={onAdd} disabled={saving} style={styles.addButton}>
          <Text style={styles.addText}>Add</Text>
        </Pressable>
      </View>

      <Text style={styles.label}>Blocked sites ({domains.length})</Text>
      <View style={styles.list}>
        {domains.length === 0 ? <Text style={styles.empty}>No domains blocked yet.</Text> : null}
        {domains.map((domain) => (
          <View key={domain} style={styles.itemRow}>
            <Text style={styles.domain}>{domain}</Text>
            <Pressable onPress={() => onRemove(domain)} style={styles.removeButton}>
              <Text style={styles.removeText}>Remove</Text>
            </Pressable>
          </View>
        ))}
      </View>

      <View style={styles.advancedBox}>
        <Text style={styles.advancedTitle}>Google & YouTube Safe Mode</Text>
        <Text style={styles.advancedHint}>
          Forces Google SafeSearch and YouTube Restricted Mode (strict) in browsers and most apps. Sites stay
          reachable — explicit results / videos are filtered by Google. Does not replace Adult site blocking.
        </Text>
        <View style={styles.advancedRow}>
          <Text style={styles.advancedLabel}>Safe Mode (Google + YouTube)</Text>
          <Switch
            value={safeMode}
            onValueChange={onToggleSafeMode}
            disabled={saving}
            trackColor={{ false: "#d1d5db", true: "#5eead4" }}
            thumbColor="#fff"
          />
        </View>
      </View>

      <View style={styles.advancedBox}>
        <Text style={styles.advancedTitle}>Advanced</Text>
        <Text style={styles.advancedHint}>
          Extra DNS keyword block for anime/manga-style hostnames only (not “movie” / “cartoon”). Matches DNS names (
          <Text style={{ fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace" }}>{advancedKeywordsPreview}</Text>
          ); not page titles or paths.
        </Text>
        <View style={styles.advancedRow}>
          <Text style={styles.advancedLabel}>Anime / manga hostname filter</Text>
          <Switch
            value={advancedMediaKeywords}
            onValueChange={onToggleAdvancedKeywords}
            disabled={saving}
            trackColor={{ false: "#d1d5db", true: "#a78bfa" }}
            thumbColor="#fff"
          />
        </View>
        <Text style={styles.advancedWarn}>
          Optional. Leave off for Adult-only. Add youtube.com (or other sites) above to block them in the browser.
        </Text>
      </View>

      <LockboxLabPanel />

      {Platform.OS === "ios" && iosBlockingUiEnabled ? <FamilyControlsBlockingSection /> : null}
      {Platform.OS === "ios" && !iosBlockingUiEnabled ? (
        <View style={styles.iosBlockedStub}>
          <Text style={styles.iosBlockedTitle}>iOS app blocking (Screen Time)</Text>
          <Text style={styles.iosBlockedBody}>
            Hidden until native Family Controls is wired. Use domain filtering above for now. Set
            extra.iosFamilyControlsBlockingUiEnabled to true in app.json when you are ready.
          </Text>
        </View>
      ) : null}

      {Platform.OS === "ios" ? (
        <View style={styles.iosBox}>
          <Text style={styles.iosTitle}>iOS setup (DNS profile)</Text>
          <Text style={styles.instructions}>1) Install DNS profile</Text>
          <Text style={styles.instructions}>2) Open NextDNS / configuration URL</Text>
          <TextInput
            value={nextDnsToken}
            onChangeText={setToken}
            placeholder="NextDNS token (optional)"
            autoCapitalize="none"
            style={styles.input}
          />
          <View style={styles.buttonRow}>
            <Pressable onPress={onSaveToken} style={styles.secondaryButton}>
              <Text style={styles.secondaryText}>Save token</Text>
            </Pressable>
            <Pressable onPress={openProfile} style={styles.addButton}>
              <Text style={styles.addText}>Open URL</Text>
            </Pressable>
          </View>
          <Text style={styles.hint}>
            Using iOS profile flow only. No VPN bypass tricks. Domain-level filtering only.
          </Text>
        </View>
      ) : null}

      <Text style={styles.privacy}>
        Privacy: stores blocklist domains, filter toggles, and optional advanced DNS keyword presets. Does not log
        visited URLs or page content.
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { marginTop: 16, padding: 14, borderRadius: 12, backgroundColor: "#f3f4f6" },
  title: { fontSize: 18, fontWeight: "700", color: "#1f2937", marginBottom: 6 },
  lockboxHint: { fontSize: 12, color: "#4338ca", marginBottom: 12, lineHeight: 18 },
  row: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  label: { color: "#374151", fontWeight: "600", marginBottom: 6 },
  toggle: { borderRadius: 999, backgroundColor: "#6b7280", paddingHorizontal: 14, paddingVertical: 6 },
  toggleOn: { backgroundColor: "#059669" },
  toggleText: { color: "#fff", fontWeight: "700" },
  inputRow: { flexDirection: "row", gap: 8, marginBottom: 12 },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: "#fff",
  },
  addButton: { borderRadius: 8, backgroundColor: "#2563eb", paddingHorizontal: 12, justifyContent: "center" },
  addText: { color: "#fff", fontWeight: "700" },
  list: { borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, backgroundColor: "#fff", padding: 8 },
  itemRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 6 },
  domain: { color: "#111827", fontSize: 13 },
  removeButton: { borderRadius: 8, backgroundColor: "#fee2e2", paddingHorizontal: 10, paddingVertical: 5 },
  removeText: { color: "#991b1b", fontWeight: "700", fontSize: 12 },
  empty: { color: "#6b7280", fontStyle: "italic" },
  iosBox: { marginTop: 14, borderTopWidth: 1, borderTopColor: "#d1d5db", paddingTop: 12 },
  iosTitle: { fontWeight: "700", color: "#1f2937", marginBottom: 6 },
  instructions: { color: "#374151", fontSize: 13 },
  buttonRow: { marginTop: 10, flexDirection: "row", gap: 8 },
  secondaryButton: { borderRadius: 8, backgroundColor: "#e5e7eb", paddingHorizontal: 12, justifyContent: "center" },
  secondaryText: { color: "#111827", fontWeight: "700" },
  hint: { marginTop: 8, color: "#6b7280", fontSize: 12 },
  privacy: { marginTop: 12, color: "#4b5563", fontSize: 12 },
  iosBlockedStub: {
    marginTop: 14,
    borderTopWidth: 1,
    borderTopColor: "#d1d5db",
    paddingTop: 12,
    paddingBottom: 4,
  },
  iosBlockedTitle: { fontWeight: "700", color: "#6b7280", marginBottom: 6, fontSize: 14 },
  iosBlockedBody: { color: "#9ca3af", fontSize: 12, lineHeight: 18 },
  advancedBox: {
    marginTop: 14,
    borderTopWidth: 1,
    borderTopColor: "#d1d5db",
    paddingTop: 12,
  },
  advancedTitle: { fontSize: 16, fontWeight: "700", color: "#1f2937", marginBottom: 6 },
  advancedHint: { color: "#4b5563", fontSize: 12, lineHeight: 18, marginBottom: 10 },
  advancedRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  advancedLabel: { color: "#374151", fontWeight: "600", flex: 1, paddingRight: 12 },
  advancedWarn: { color: "#92400e", fontSize: 11, lineHeight: 16 },
});
